// src/momentsService.js
// Centralized service for the new moments table
import { supabase } from './supabaseClient'

// Save a new moment to the moments table
export async function saveMoment({ userId, imageUrl, text, locationName, locationLat, locationLng, city, dayNumber }) {
  const { data, error } = await supabase
    .from('moments')
    .insert({
      user_id: userId,
      image_url: imageUrl,
      text: text || null,
      location_name: locationName || null,
      location_lat: locationLat || null,
      location_lng: locationLng || null,
      city: city || null,
      day_number: dayNumber || null,
    })
    .select()
    .single()
  return { data, error }
}

// Fetch all moments for a user, newest first
export async function fetchMoments(userId) {
  const { data, error } = await supabase
    .from('moments')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  return { data: data || [], error }
}

// Delete a moment
export async function deleteMoment(momentId) {
  const { error } = await supabase.from('moments').delete().eq('id', momentId)
  return { error }
}

// Update moment text
export async function updateMomentText(momentId, text) {
  const { error } = await supabase.from('moments').update({ text }).eq('id', momentId)
  return { error }
}

// Add moment to today's gratitude list
// Finds the next empty gratitude slot for the given station and fills it
export async function addMomentToGratitudes({ userId, city, dayNumber, momentText }) {
  // Fetch current entry
  const { data: entry, error: fetchErr } = await supabase
    .from('journal_entries')
    .select('gratitudes, images')
    .eq('user_id', userId)
    .eq('city', city)
    .eq('day_number', dayNumber)
    .single()

  if (fetchErr) return { error: fetchErr }

  const gratitudes = [...(entry?.gratitudes || Array(10).fill(''))]
  // Find first empty slot
  const emptyIdx = gratitudes.findIndex(g => !g?.trim())
  if (emptyIdx === -1) return { error: { message: 'All 10 gratitude slots are full' }, full: true }

  gratitudes[emptyIdx] = momentText || ''

  const { error } = await supabase
    .from('journal_entries')
    .update({ gratitudes })
    .eq('user_id', userId)
    .eq('city', city)
    .eq('day_number', dayNumber)

  return { error, slotIndex: emptyIdx, previousGratitudes: entry?.gratitudes }
}

// Undo adding to gratitudes — restore previous value
export async function undoAddToGratitudes({ userId, city, dayNumber, previousGratitudes }) {
  const { error } = await supabase
    .from('journal_entries')
    .update({ gratitudes: previousGratitudes })
    .eq('user_id', userId)
    .eq('city', city)
    .eq('day_number', dayNumber)
  return { error }
}

// Migrate existing images from journal_entries into moments table
export async function migrateExistingMoments(userId) {
  // Check if already migrated successfully
  const migrated = localStorage.getItem(`moments_migrated_${userId}`)
  if (migrated === 'done') return { skipped: true }

  // Fetch existing journal entries with images
  const { data: entries, error } = await supabase
    .from('journal_entries')
    .select('city, day_number, images, gratitudes, updated_at')
    .eq('user_id', userId)
    .not('images', 'is', null)

  if (error || !entries?.length) {
    localStorage.setItem(`moments_migrated_${userId}`, 'done')
    return { migrated: 0 }
  }

  // Check which city+day combos already exist in moments table
  const { data: existing } = await supabase
    .from('moments')
    .select('city, day_number')
    .eq('user_id', userId)

  const existingKeys = new Set((existing || []).map(e => `${e.city}_${e.day_number}`))

  const toInsert = entries
    .filter(e => e.images?.[0] && !existingKeys.has(`${e.city}_${e.day_number}`))
    .map(e => ({
      user_id: userId,
      image_url: e.images[0],
      text: e.gratitudes?.[0] || null,
      city: e.city,
      day_number: e.day_number,
      created_at: e.updated_at,
    }))

  if (!toInsert.length) {
    localStorage.setItem(`moments_migrated_${userId}`, 'done')
    return { migrated: 0 }
  }

  const { error: insertErr } = await supabase.from('moments').insert(toInsert)

  if (!insertErr) {
    localStorage.setItem(`moments_migrated_${userId}`, 'done')
    return { migrated: toInsert.length }
  }

  // Don't mark done if it failed — will retry next time
  console.error('Migration failed:', insertErr.message)
  return { migrated: 0, error: insertErr }
}
